/* eslint-disable no-undef */
/* =============================================================================
 * Usage — water usage over time, drillable portfolio -> company -> property -> unit.
 * Hour / day / month buckets sourced from raw meter pulses (/api/usage). Shows actual
 * vs profile-estimated (gap-filled) usage, the trend, top users, and reporting-gap
 * drift callouts prompting a manual re-baseline. Read-only.
 * ===========================================================================*/
import React, { useState, useEffect, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./components.jsx";
import { fetchUsage, fetchQboConnections, fetchProperties, fetchUnits } from "./api.js";

const fmtHcf = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: 1 }));
const fmtHcf0 = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 }));
// value formatter: keeps precision for small (single-unit) usage but stays clean for large (portfolio) totals
const fmtHcfV = (n) => (n == null ? "—" : Math.abs(+n) >= 100 ? Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 }) : Math.abs(+n) >= 1 ? (+n).toFixed(1) : (+n).toFixed(2));

// period strings: "2026-06" (month), "2026-06-15" (day), "2026-06-15 14" (hour)
function parsePeriod(period) {
  const [dp, hp] = String(period).split(" ");
  const [y, mo, d] = dp.split("-").map(Number);
  return new Date(y, (mo || 1) - 1, d || 1, hp != null ? +hp : 0);
}
function periodLabel(period, grain) {
  if (!period) return "";
  const d = parsePeriod(period);
  if (grain === "hour") { const h = d.getHours(); return `${((h + 11) % 12) + 1}${h < 12 ? "a" : "p"}`; }
  if (grain === "day") return `${d.getMonth() + 1}/${d.getDate()}`;
  return d.toLocaleString("en-US", { month: "short" });
}
function periodLong(period, grain) {
  if (!period) return "";
  const d = parsePeriod(period);
  if (grain === "hour") return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric" });
  if (grain === "day") return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return d.toLocaleString("en-US", { month: "short", year: "numeric" });
}

// Searchable pill picker: a styled trigger + a portaled, type-to-filter list (same portal / outside-click
// pattern as QboRefPicker, generic over {value,label} options). Fixed width => the filter bar never jumps.
function SearchPicker({ value, options, onChange, icon, width = 200 }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rect, setRect] = useState(null);
  const ref = useRef();
  const dropRef = useRef();

  useEffect(() => {
    const h = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!open) return;
    const place = () => { if (ref.current) setRect(ref.current.getBoundingClientRect()); };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [open]);

  const cur = options.find((o) => String(o.value) === String(value)) || options[0];
  const s = q.trim().toLowerCase();
  const filtered = (s ? options.filter((o) => o.label.toLowerCase().includes(s)) : options).slice(0, 150);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button"
              onClick={() => { if (!open && ref.current) setRect(ref.current.getBoundingClientRect()); setQ(""); setOpen((o) => !o); }}
              style={{ width, height: 36, display: "inline-flex", alignItems: "center", gap: 7, padding: "0 11px",
                       borderRadius: 9, border: `1px solid ${open ? "var(--color-primary)" : "var(--color-border)"}`,
                       background: "var(--color-surface)", font: "inherit", fontSize: 13, color: "var(--color-text)",
                       cursor: "pointer", boxShadow: open ? "0 0 0 2px var(--color-primary-tint)" : "none" }}>
        {icon && <Icon name={icon} size={14} />}
        <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cur ? cur.label : ""}</span>
        <span style={{ opacity: 0.55, display: "inline-flex" }}><Icon name="chevronDown" size={14} /></span>
      </button>
      {open && rect && createPortal(
        <div ref={dropRef}
             style={{ position: "fixed", zIndex: 9999, top: rect.bottom + 5, left: rect.left, width: Math.max(rect.width, 240),
                      background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 10,
                      boxShadow: "0 12px 30px rgba(0,0,0,.16)", overflow: "hidden" }}>
          <input autoFocus className="input" style={{ margin: 7, width: "calc(100% - 14px)" }}
                 placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
          <div style={{ maxHeight: 260, overflowY: "auto", padding: 5 }}>
            {filtered.length === 0 ? (
              <div className="muted" style={{ padding: "8px 10px", fontSize: 13 }}>No matches.</div>
            ) : filtered.map((o) => {
              const on = String(o.value) === String(value);
              return (
                <div key={o.value || "all"} onClick={() => { onChange(o.value); setOpen(false); setQ(""); }}
                     style={{ padding: "8px 10px", borderRadius: 7, fontSize: 13.5, cursor: "pointer",
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                              background: on ? "var(--color-primary)" : "transparent", color: on ? "#fff" : "var(--color-text-secondary)" }}
                     onMouseEnter={(e) => { if (!on) e.currentTarget.style.background = "var(--color-surface-hover)"; }}
                     onMouseLeave={(e) => { if (!on) e.currentTarget.style.background = "transparent"; }}>
                  {o.label}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export function Analytics({ mgr, go, params }) {
  const [grain, setGrain] = useState("month");          // month | day | hour
  const [hourPoints, setHourPoints] = useState(72);
  const [dayPoints, setDayPoints] = useState(60);
  const [monthPoints, setMonthPoints] = useState(13);
  const [offset, setOffset] = useState(0);   // how many windows back from the latest (0 = current)
  const [realmId, setRealmId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [tenancyId, setTenancyId] = useState("");
  const [companies, setCompanies] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const scopeMgr = mgr && mgr !== "all" ? mgr : null;
  const points = grain === "hour" ? hourPoints : grain === "day" ? dayPoints : monthPoints;
  const grainWord = grain === "hour" ? "hourly" : grain === "day" ? "daily" : "monthly";
  const unitWord = grain === "hour" ? "hour" : grain === "day" ? "day" : "month";

  // scope option lists — react to the manager switcher, and reset any narrower selection
  useEffect(() => {
    fetchQboConnections(scopeMgr).then((c) => setCompanies(c || [])).catch(() => setCompanies([]));
    fetchProperties().then((p) => setProperties(p || [])).catch(() => setProperties([]));
    fetchUnits().then((u) => setUnits(u || [])).catch(() => setUnits([]));
    setRealmId(""); setPropertyId(""); setTenancyId("");
  }, [mgr]);

  useEffect(() => { setPropertyId(""); setTenancyId(""); }, [realmId]);
  useEffect(() => { setTenancyId(""); }, [propertyId]);

  // Drilled in from Properties & Units ("Usage" on a unit or property): the tree picked the scope, so adopt it —
  // you shouldn't have to re-choose in the dropdowns what you already navigated to. Declared AFTER the cascade
  // resets above so it wins on mount, and only the NARROWEST level is set (also setting a wider one would trip
  // those resets and clear it). Arriving with no params (the nav link) means a fresh, portfolio-wide view; a
  // manual dropdown change doesn't touch params, so it sticks.
  useEffect(() => {
    const t = params?.tenancyId, p = params?.propertyId, r = params?.realmId;
    if (t != null) setTenancyId(String(t));
    else if (p != null) setPropertyId(String(p));
    else if (r != null) setRealmId(String(r));
    else { setRealmId(""); setPropertyId(""); setTenancyId(""); }
  }, [params?.tenancyId, params?.propertyId, params?.realmId]);
  useEffect(() => { setOffset(0); }, [grain, points, mgr, realmId, propertyId, tenancyId]);   // reset to the latest window on any change

  useEffect(() => {
    let ok = true; setLoading(true);
    fetchUsage({ managerId: scopeMgr, realmId, propertyId, tenancyId, granularity: grain, points, offset })
      .then((r) => { if (ok) setReport(r); })
      .catch(() => { if (ok) setReport(null); })
      .finally(() => { if (ok) setLoading(false); });
    return () => { ok = false; };
  }, [mgr, realmId, propertyId, tenancyId, grain, points, offset]);

  // cascading dropdown options (manager -> company -> property)
  const inMgr = (x) => !scopeMgr || String(x.managerId) === String(scopeMgr);
  const propOpts = properties.filter((p) => inMgr(p) && (!realmId || String(p.realmId) === String(realmId)));
  // one entry per physical unit — /api/units returns a row per tenancy, so a unit with several tenants would repeat
  const unitOpts = (() => {
    const seen = new Set();
    return units
      .filter((u) => inMgr(u) && (!realmId || String(u.realmId) === String(realmId)) && (!propertyId || String(u.propKey) === String(propertyId)))
      .sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0))   // prefer an active tenancy as the unit's stand-in
      .filter((u) => { const k = `${u.propKey}::${u.name}`; if (seen.has(k)) return false; seen.add(k); return true; });
  })();

  const companyPickOpts = [{ value: "", label: "All companies" }, ...companies.map((c) => ({ value: String(c.realmId), label: c.companyName || c.realmId }))];
  const propertyPickOpts = [{ value: "", label: "All properties" }, ...propOpts.map((p) => ({ value: String(p.id), label: p.name }))];
  const unitPickOpts = [{ value: "", label: "All units" }, ...unitOpts.map((u) => ({ value: String(u.id), label: `${u.propName} · ${u.name}` }))];

  const series = report?.series || [];
  const topUnits = report?.topUnits || [];
  const gaps = report?.gaps || [];
  const maxUsage = Math.max(0.01, ...series.map((s) => s.usage + s.estUsage));   // scale to the actual peak (usage can be < 1 HCF for one unit)
  const maxTop = Math.max(1, ...topUnits.map((u) => u.usage));

  const totActual = series.reduce((a, s) => a + s.usage, 0);
  const totEst = series.reduce((a, s) => a + s.estUsage, 0);
  const pctEst = totActual + totEst > 0 ? (totEst / (totActual + totEst)) * 100 : 0;

  const last = series[series.length - 1];
  const prev = series[series.length - 2];
  const trend = last && prev && prev.usage > 0 ? ((last.usage - prev.usage) / prev.usage) * 100 : null;
  const peak = series.reduce((a, b) => (b.usage > (a ? a.usage : -1) ? b : a), null);
  const labelStride = Math.max(1, Math.ceil(series.length / 14));   // keep the x-axis uncrowded
  const tight = series.length > 30;
  const ESTBG = "repeating-linear-gradient(45deg, var(--color-warning-text) 0 2px, var(--color-warning-bg) 2px 5px)";
  // Full years in the window label — "Jul 25 – Jul 26" read as two DAYS when it meant Jul 2025 → Jul 2026.
  // Day/hour grains add the year only when the window actually crosses a year boundary.
  const winFmt = (p, withYear) => { const d = parsePeriod(p); return grain === "month" ? d.toLocaleString("en-US", { month: "short", year: "numeric" }) : d.toLocaleString("en-US", withYear ? { month: "short", day: "numeric", year: "numeric" } : { month: "short", day: "numeric" }); };
  const windowLabel = series.length ? (() => {
    const crossYear = parsePeriod(series[0].period).getFullYear() !== parsePeriod(series[series.length - 1].period).getFullYear();
    return `${winFmt(series[0].period, crossYear)} – ${winFmt(series[series.length - 1].period, crossYear)}`;
  })() : "";

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>Usage</h1>
          <p>Water usage over time · HCF · {grainWord} · from raw meter pulses · drill portfolio → unit</p>
        </div>
        <div className="right" style={{ gap: 8, flexWrap: "wrap" }}>
          <SearchPicker value={realmId} options={companyPickOpts} onChange={setRealmId} icon="link" width={210} />
          <SearchPicker value={propertyId} options={propertyPickOpts} onChange={setPropertyId} icon="building" width={180} />
          <SearchPicker value={tenancyId} options={unitPickOpts} onChange={setTenancyId} icon="droplet" width={168} />
          <div className="seg">
            <button className={grain === "month" ? "on" : ""} onClick={() => setGrain("month")}>Month</button>
            <button className={grain === "day" ? "on" : ""} onClick={() => setGrain("day")}>Day</button>
            <button className={grain === "hour" ? "on" : ""} onClick={() => setGrain("hour")}>Hour</button>
          </div>
          {/* All three grains' range sets occupy the SAME grid cell; only the active one is visible. The box is
              therefore always exactly as wide as the widest set — no measured min-width to get wrong — so
              switching Month/Day/Hour never resizes the segment or shoves the control bar. */}
          <div style={{ display: "grid" }} title="Time range">
            {[["month", [[6, "6mo"], [13, "12mo"], [25, "24mo"], [37, "3yr"]], monthPoints, setMonthPoints],
              ["day", [[30, "30d"], [60, "60d"], [90, "90d"]], dayPoints, setDayPoints],
              ["hour", [[24, "24h"], [48, "48h"], [72, "72h"], [168, "7d"]], hourPoints, setHourPoints]].map(([g, set, cur, setCur]) => (
              <div key={g} className="seg" style={{ gridArea: "1 / 1", justifyContent: "center", visibility: grain === g ? "visible" : "hidden" }}>
                {set.map(([v, l]) => <button key={v} className={cur === v ? "on" : ""} onClick={() => setCur(v)}>{l}</button>)}
              </div>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card"><div className="empty">Loading usage…</div></div>
      ) : series.length === 0 ? (
        <div className="card"><div className="empty">No usage in range for this scope.</div></div>
      ) : (
        <Fragment>
          {gaps.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              {gaps.slice(0, 3).map((g, i) => (
                // A unit can appear twice (a closed gap AND ongoing silence), so unitId alone is not unique.
                <div key={`${g.unitId}-${g.ongoing ? "now" : g.lastGapEnd || i}`} className="card" style={{ padding: "12px 15px", display: "flex", gap: 12, alignItems: "flex-start",
                     background: "var(--color-warning-bg)", border: "1px solid var(--color-warning-text)", boxShadow: "none" }}>
                  <span style={{ color: "var(--color-warning-text)", marginTop: 1 }}><Icon name="alert" size={18} /></span>
                  <div style={{ flex: 1, fontSize: 13 }}>
                    <b>{g.property} · {g.unit}</b>{g.ongoing
                      ? ` — sensor has been silent ${g.gapDays} day${g.gapDays !== 1 ? "s" : ""}${g.lastGapEnd ? ` (since ${g.lastGapEnd})` : ""}, drifting below the real meter`
                      : ` may be drifting below the real meter — ${g.gapDays}-day reporting gap${g.lastGapEnd ? ` (through ${g.lastGapEnd})` : ""}`}
                    {g.tooLong ? ", too long to estimate reliably" : g.estMissedHcf > 0 ? `, ~${fmtHcf(g.estMissedHcf)} HCF likely un-counted` : ""}.
                    <span className="muted"> Take a manual reading to confirm &amp; re-baseline.</span>
                  </div>
                  {go && <button className="btn btn--soft btn--sm" onClick={() => go("properties")} style={{ whiteSpace: "nowrap" }}>Go to units</button>}
                </div>
              ))}
              {gaps.length > 3 && <div className="muted" style={{ fontSize: 12 }}>+{gaps.length - 3} more unit{gaps.length - 3 !== 1 ? "s" : ""} with reporting gaps.</div>}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 14 }}>
            <Metric label={`This ${unitWord}${last ? ` (${periodLong(last.period, grain)})` : ""}`}
                    value={<span>{fmtHcfV(last && last.usage)} <span className="muted" style={{ fontSize: 13 }}>HCF</span></span>} />
            <Metric label={`vs previous ${unitWord}`}
                    value={trend == null ? "—" :
                      <span style={{ color: trend > 0 ? "var(--color-warning-text)" : "var(--color-success)" }}>
                        {trend > 0 ? "▲" : "▼"} {trend > 0 ? "+" : ""}{trend.toFixed(0)}%
                      </span>} />
            <Metric label="Peak in range" value={fmtHcfV(peak && peak.usage)} sub={peak ? periodLong(peak.period, grain) : ""} />
            <Metric label="Estimated (gaps)"
                    value={<span style={{ color: pctEst > 0 ? "var(--color-warning-text)" : undefined }}>{pctEst.toFixed(0)}%</span>}
                    sub={`${gaps.length} reporting gap${gaps.length !== 1 ? "s" : ""} in range`} />
          </div>

          <div className="card" style={{ padding: "16px 18px", marginBottom: 14 }}>
            <div className="between" style={{ marginBottom: 12 }}>
              <div className="row" style={{ gap: 10, alignItems: "center" }}>
                <b style={{ fontSize: 14 }}>{grainWord[0].toUpperCase() + grainWord.slice(1)} usage</b>
                <span className="row" style={{ gap: 2, alignItems: "center" }}>
                  <button className="iconbtn" title="Earlier period" onClick={() => setOffset((o) => o + 1)} style={{ padding: "0 7px", fontSize: 16, lineHeight: 1 }}>‹</button>
                  <span className="muted" style={{ fontSize: 11.5, minWidth: 120, textAlign: "center" }}>{windowLabel}</span>
                  <button className="iconbtn" title="Later period" onClick={() => setOffset((o) => Math.max(0, o - 1))} disabled={offset === 0} style={{ padding: "0 7px", fontSize: 16, lineHeight: 1, opacity: offset === 0 ? 0.35 : 1 }}>›</button>
                </span>
              </div>
              <div className="muted" style={{ fontSize: 12, display: "flex", gap: 14 }}>
                <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "var(--color-primary)" }} /> actual</span>
                <span><span style={{ display: "inline-block", width: 12, height: 9, borderRadius: 2, background: ESTBG }} /> estimated</span>
              </div>
            </div>
            <div style={{ position: "relative", height: 190, paddingLeft: 36 }}>
              {/* y gridlines + scale labels (peak and half); baseline is 0 */}
              {[1, 0.5].map((f) => (
                <div key={f} style={{ position: "absolute", left: 36, right: 0, bottom: `${f * 100}%`, borderTop: "1px dashed var(--color-border-light)" }}>
                  <span style={{ position: "absolute", left: -34, top: -7, fontSize: 9.5, color: "var(--color-muted)" }}>{fmtHcfV(maxUsage * f)}</span>
                </div>
              ))}
              <span style={{ position: "absolute", left: 2, bottom: -1, fontSize: 9.5, color: "var(--color-muted)" }}>0</span>
              {/* bars: each fills from the baseline up — actual (bottom) + estimated (top), sized as % of the bar */}
              <div style={{ display: "flex", alignItems: "flex-end", gap: tight ? 2 : 6, height: "100%" }}>
                {series.map((s) => {
                  const total = s.usage + s.estUsage;
                  return (
                    <div key={s.period} title={`${periodLong(s.period, grain)} · ${fmtHcf(s.usage)} HCF${s.estUsage > 0 ? ` (+${fmtHcf(s.estUsage)} est)` : ""}`}
                         style={{ flex: 1, minWidth: 0, height: `${total > 0 ? Math.max(1.5, (total / maxUsage) * 100) : 0}%`,
                                  borderRadius: "2px 2px 0 0", overflow: "hidden" }}>
                      {s.estUsage > 0 && <div style={{ height: `${(s.estUsage / total) * 100}%`, background: ESTBG }} />}
                      {s.usage > 0 && <div style={{ height: `${(s.usage / total) * 100}%`, background: "var(--color-primary)" }} />}
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ display: "flex", gap: tight ? 2 : 6, marginTop: 6, paddingLeft: 36 }}>
              {series.map((s, i) => {
                let label = "";
                if (grain === "hour") {
                  const d = parsePeriod(s.period);
                  const prev = i > 0 ? parsePeriod(series[i - 1].period) : null;
                  if (!prev || d.getDate() !== prev.getDate()) label = d.toLocaleString("en-US", { month: "short", day: "numeric" });   // day boundary → date
                  else if (i % labelStride === 0) { const h = d.getHours(); label = `${((h + 11) % 12) + 1}${h < 12 ? "a" : "p"}`; }   // hour marks within a day
                } else if (i % labelStride === 0) {
                  label = periodLabel(s.period, grain);
                }
                return <span key={s.period} style={{ flex: 1, minWidth: 0, textAlign: "center", fontSize: 10.5, color: "var(--color-muted)", whiteSpace: "nowrap", overflow: "hidden" }}>{label}</span>;
              })}
            </div>
          </div>

          <div className="card" style={{ padding: "16px 18px" }}>
            <b style={{ fontSize: 14 }}>Top users · this range</b>
            <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 12 }}>
              {topUnits.length === 0 ? <div className="muted">No usage in range.</div> : topUnits.map((u) => (
                <div key={u.unitId} className="row" style={{ gap: 10 }}>
                  <span style={{ width: 175, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    <b>{u.property}</b> <span className="muted">· {u.unit}</span>
                  </span>
                  <span style={{ flex: 1, height: 16, background: "var(--color-surface-hover)", borderRadius: 4, overflow: "hidden" }}>
                    <span style={{ display: "block", height: "100%", width: `${Math.max(2, (u.usage / maxTop) * 100)}%`, background: "var(--color-primary)" }} />
                  </span>
                  <span style={{ width: 90, textAlign: "right", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>{fmtHcfV(u.usage)} HCF</span>
                </div>
              ))}
            </div>
          </div>
        </Fragment>
      )}
    </div>
  );
}

function Metric({ label, value, sub }) {
  return (
    <div className="card" style={{ padding: "14px 16px" }}>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, marginTop: 2, lineHeight: 1.2 }}>{value}</div>
      {sub ? <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}
